import i18n from '@/i18n'
import createClient from 'openapi-fetch'

import { clearStoredAuth, readStoredAuth, writeStoredAuth } from '@/features/auth/session'
import { env } from '@/shared/config/env'
import type { paths } from './schema'

export type ApiError = {
  status?: number
  requestId?: string
  message: string
  translation?: string
  fieldErrors?: Record<string, string[]>
  nonFieldErrors?: string[]
  response?: {
    status?: number
    data?: Record<string, unknown>
  }
}

type ApiErrorTranslator = (key: string) => string | undefined

export function apiErrorMessage(error: unknown, fallback: string, translate?: ApiErrorTranslator): string {
  if (!error || typeof error !== 'object') return fallback

  const apiError = error as Partial<ApiError>
  if (apiError.translation) {
    const translated = translate?.(apiError.translation)
    if (translated) return translated
  }

  const nonFieldError = apiError.nonFieldErrors?.find(Boolean)
  if (nonFieldError) return nonFieldError

  const fieldError = Object.values(apiError.fieldErrors ?? {})
    .flat()
    .find(Boolean)
  if (fieldError) return fieldError

  const isNormalizedError = apiError.status !== undefined || apiError.response !== undefined
  return isNormalizedError && apiError.message ? apiError.message : fallback
}

export const apiClient = createClient<paths>({ baseUrl: env.apiBaseUrl })

let refreshTokenRequest: Promise<boolean> | null = null

apiClient.use({
  onRequest({ request }) {
    const stored = readStoredAuth()
    if (stored?.accessToken) request.headers.set('Authorization', `Bearer ${stored.accessToken}`)
    return request
  },
})

type ApiResult<T> = { data?: T; error?: unknown; response: Response }
type ApiErrorBody = {
  detail?: string
  message?: string
  translation?: string
  errors?: unknown
  non_field_errors?: unknown
  [key: string]: unknown
}

const metadataKeys = new Set([
  'code',
  'detail',
  'error',
  'errors',
  'message',
  'nonFieldErrors',
  'non_field_errors',
  'requestId',
  'request_id',
  'status',
  'status_code',
  'traceId',
  'trace_id',
  'translation',
  'type',
])

function messages(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string') return [value]
  if (value == null) return []
  return [String(value)]
}

export function normalizeApiError(error: unknown, response?: Response, fallback = i18n.global.t('api.request_failed')): ApiError {
  const body = typeof error === 'object' && error && 'content' in error ? error.content : error
  const fieldErrors: Record<string, string[]> = {}
  let nonFieldErrors: string[] = []
  let translation: string | undefined

  if (typeof body === 'object' && body) {
    const typed = body as ApiErrorBody
    translation = typeof typed.translation === 'string' ? typed.translation : undefined
    const source =
      typed.errors && typeof typed.errors === 'object' && !Array.isArray(typed.errors)
        ? (typed.errors as Record<string, unknown>)
        : (typed as Record<string, unknown>)
    if (Array.isArray(typed.errors) || typeof typed.errors === 'string') {
      nonFieldErrors = messages(typed.errors)
    }
    Object.entries(source).forEach(([key, value]) => {
      if (key === 'non_field_errors' || key === 'nonFieldErrors') {
        nonFieldErrors = messages(value)
      } else if (!metadataKeys.has(key)) {
        const values = messages(value)
        if (values.length) fieldErrors[key] = values
      }
    })
  }

  const message =
    nonFieldErrors[0] ??
    (typeof body === 'object' && body
      ? String((body as ApiErrorBody).detail ?? (body as ApiErrorBody).message ?? fallback)
      : typeof body === 'string'
        ? body
        : fallback)
  const requestId = response?.headers.get('X-Request-ID') ?? undefined

  return {
    ...(response ? { status: response.status } : {}),
    ...(requestId ? { requestId } : {}),
    message,
    ...(translation ? { translation } : {}),
    ...(Object.keys(fieldErrors).length ? { fieldErrors } : {}),
    ...(nonFieldErrors.length ? { nonFieldErrors } : {}),
    response: {
      ...(response ? { status: response.status } : {}),
      data: typeof body === 'object' && body ? (body as Record<string, unknown>) : { non_field_errors: [message] },
    },
  }
}

async function performTokenRefresh(): Promise<boolean> {
  const stored = readStoredAuth()
  if (!stored?.refreshToken) return false
  try {
    const response = await fetch(`${env.apiBaseUrl}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: stored.refreshToken }),
    })
    if (!response.ok) throw new Error('Token refresh failed')
    const body = (await response.json()) as { access?: unknown }
    if (typeof body.access !== 'string') throw new Error('Refresh response has no access token')
    writeStoredAuth({ ...stored, accessToken: body.access })
    window.dispatchEvent(new CustomEvent('auth:refreshed'))
    return true
  } catch {
    clearStoredAuth()
    window.dispatchEvent(new CustomEvent('auth:expired'))
    return false
  }
}

export async function refreshTokens(): Promise<boolean> {
  refreshTokenRequest ??= performTokenRefresh().finally(() => {
    refreshTokenRequest = null
  })
  return refreshTokenRequest
}

export async function apiRequest<T>(call: () => Promise<ApiResult<T>>, fallback = i18n.global.t('api.request_failed')): Promise<T> {
  let result = await call()
  if (result.response.status === 401 && (await refreshTokens())) result = await call()
  if (result.error || result.data === undefined) {
    if (result.response.status === 401) clearStoredAuth()
    throw normalizeApiError(result.error, result.response, fallback)
  }
  return result.data
}

export async function apiVoidRequest(call: () => Promise<ApiResult<unknown>>, fallback = i18n.global.t('api.request_failed')) {
  let result = await call()
  if (result.response.status === 401 && (await refreshTokens())) result = await call()
  if (result.error) throw normalizeApiError(result.error, result.response, fallback)
}

type QueryValue = string | number | boolean | null | undefined | readonly string[]

export type RawApiOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  query?: Record<string, QueryValue>
  body?: unknown
  responseType?: 'json' | 'blob' | 'text'
}

function apiUrl(path: string, query?: Record<string, QueryValue>) {
  const base = env.apiBaseUrl.endsWith('/') ? env.apiBaseUrl : `${env.apiBaseUrl}/`
  const url = new URL(path.replace(/^\//, ''), base)
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value == null || value === '') return
    if (Array.isArray(value)) value.forEach((item) => url.searchParams.append(key, item))
    else url.searchParams.set(key, String(value))
  })
  return url
}

async function rawFetch(path: string, options: RawApiOptions) {
  const stored = readStoredAuth()
  const headers = new Headers()
  if (stored?.accessToken) headers.set('Authorization', `Bearer ${stored.accessToken}`)
  let body: BodyInit | undefined
  if (options.body instanceof FormData || options.body instanceof Blob || typeof options.body === 'string') {
    body = options.body
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }
  return fetch(apiUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    ...(body !== undefined ? { body } : {}),
  })
}

export async function rawApiRequest<T>(path: string, options: RawApiOptions = {}): Promise<T> {
  let response = await rawFetch(path, options)
  if (response.status === 401 && (await refreshTokens())) response = await rawFetch(path, options)

  if (!response.ok) {
    let error: unknown
    try {
      error = await response.json()
    } catch {
      error = await response.text()
    }
    throw normalizeApiError(error, response)
  }

  if (options.responseType === 'blob') return (await response.blob()) as T
  if (options.responseType === 'text') return (await response.text()) as T
  if (response.status === 204 || response.status === 205) return undefined as T

  const responseText = await response.text()
  if (!responseText) return undefined as T

  return JSON.parse(responseText) as T
}
